/**
 * Transcript-side wrapper for the shared find/replace popover (#2233).
 *
 * Splits the transcript document into `{ key, text }` segments (one per
 * text node), pipes them through `useFindReplace`, and forwards
 * jump/replace-all calls to the existing editor-context mutations so
 * Slate stays authoritative for the doc state.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useTranscript } from '@utils/providers/transcript-context.tsx';
import { useEditorContext } from '@utils/providers/editor-context.tsx';
import {
  useFindReplace,
  FindReplaceSegment,
  preserveFirstLetterCapitalisation
} from '@components/editors/shared/find-replace/use-find-replace.ts';
import { FindReplacePopover } from '@components/editors/shared/find-replace/find-replace-popover.tsx';
import type { DocumentTranscriptTextV1 } from '@utils/types/editors/transcripts/transcript.ts';

interface Props {
  onClose: () => void;
}

interface SegmentTiming {
  uttStart: number;
  uttStop: number;
  child: DocumentTranscriptTextV1;
}

/**
 * Build segments from the transcript's utterance/paragraph/text tree.
 * One segment per non-empty text node — replaces map back exactly.
 */
function useTranscriptSegments(): {
  segments: FindReplaceSegment[];
  timings: Map<string, SegmentTiming>;
} {
  const { document } = useTranscript();
  return useMemo(() => {
    const segments: FindReplaceSegment[] = [];
    const timings = new Map<string, SegmentTiming>();
    if (!document?.document) return { segments, timings };
    document.document.forEach((utt, uIdx) => {
      const uStart = typeof utt.start === 'number' ? utt.start : null;
      const uStop = typeof utt.stop === 'number' ? utt.stop : null;
      if (uStart === null || uStop === null) return;
      utt.children.forEach((para, pIdx) => {
        para.children.forEach((child, cIdx) => {
          const text = child.text ?? '';
          if (!text) return;
          const key = `${uIdx}:${pIdx}:${cIdx}`;
          segments.push({ key, text });
          timings.set(key, {
            uttStart: uStart,
            uttStop: uStop,
            child: child as DocumentTranscriptTextV1
          });
        });
      });
    });
    return { segments, timings };
  }, [document?.document]);
}

export function TranscriptFindReplaceHost({ onClose }: Props) {
  const {
    setCurrentMatch,
    focusMatch,
    caseSensitive,
    wholeWord,
    setSearch: setContextSearch
  } = useTranscript();
  const { replaceInEditorAtTimestampAndOffsets } = useEditorContext();

  const { segments, timings } = useTranscriptSegments();

  const state = useFindReplace({ segments });

  // Mirror the popover's options into the transcript context so the
  // rest of the editor (highlighting, keyboard match navigation)
  // stays in sync. Pass values through `setOption` callbacks.
  useEffect(() => {
    state.setOption('caseSensitive', Boolean(caseSensitive));
    state.setOption('wholeWord', Boolean(wholeWord));
    // We only need this to run once on mount so the popover starts in
    // the same options state the user last used inline.
  }, []);

  useEffect(() => {
    setContextSearch(state.query);
  }, [state.query, setContextSearch]);

  const handleJumpToMatch = useCallback(
    (index: number) => {
      const match = state.matches[index];
      if (!match) return;
      const timing = timings.get(match.segmentKey);
      if (!timing) return;
      setCurrentMatch(timing.child);
      focusMatch?.(timing.child);
      window.dispatchEvent(
        new CustomEvent('transcript:focusMatch', {
          detail: { start: timing.uttStart, stop: timing.uttStop }
        })
      );
    },
    [state.matches, timings, focusMatch, setCurrentMatch]
  );

  // On mount + when currentIndex changes, jump to the active match so
  // the transcript viewport follows the popover selection.
  useEffect(() => {
    if (state.current) handleJumpToMatch(state.currentIndex);
  }, [state.currentIndex, state.current?.id]);

  const handleReplaceAll = useCallback(() => {
    if (!state.query || !state.replacement || state.matches.length === 0) {
      return;
    }
    // Replace per-match so `preserveFirstLetterCapitalisation` can
    // run against each match's ORIGINAL text — the previous
    // `replaceAllWordsInEditor` path did a static regex replace and
    // dropped the first-letter preservation entirely (#2290 reopen:
    // "Preserve capitalisation of first letter does nothing in the
    // transcript editor"). Walk matches in reverse order per
    // text-node so earlier offsets stay valid; across text-nodes the
    // ordering doesn't matter since each paragraph edit is scoped to
    // its own Slate path.
    const groupedByNode = new Map<string, typeof state.matches>();
    for (const m of state.matches) {
      const list = groupedByNode.get(m.segmentKey) ?? [];
      list.push(m);
      groupedByNode.set(m.segmentKey, list);
    }
    for (const [, matches] of groupedByNode) {
      const ordered = [...matches].sort((a, b) => b.from - a.from);
      for (const m of ordered) {
        const timing = timings.get(m.segmentKey);
        if (!timing) continue;
        const start = timing.child.start;
        const stop = timing.child.stop;
        if (typeof start !== 'number' || typeof stop !== 'number') continue;
        const finalReplacement = preserveFirstLetterCapitalisation(
          m.text,
          state.replacement,
          state.preserveFirstLetterCase
        );
        replaceInEditorAtTimestampAndOffsets(
          start,
          stop,
          m.from,
          m.to,
          finalReplacement
        );
      }
    }
  }, [state, timings, replaceInEditorAtTimestampAndOffsets]);

  // Replace only the currently-highlighted match, then advance to the
  // next one. The hook's match carries the segment key (`u:p:c`
  // path) + char offsets within the matched text node; we look the
  // node's start/stop timestamps up in `timings` and route the
  // mutation through the editor context's targeted replace helper
  // (#2231 reopen: prev/next were useless because only Replace All
  // was exposed).
  const handleReplaceOne = useCallback(() => {
    if (!state.query || !state.replacement) return;
    const match = state.matches[state.currentIndex];
    if (!match) return;
    const timing = timings.get(match.segmentKey);
    if (!timing) return;
    const start = timing.child.start;
    const stop = timing.child.stop;
    if (typeof start !== 'number' || typeof stop !== 'number') return;
    const finalReplacement = preserveFirstLetterCapitalisation(
      match.text,
      state.replacement,
      state.preserveFirstLetterCase
    );
    replaceInEditorAtTimestampAndOffsets(
      start,
      stop,
      match.from,
      match.to,
      finalReplacement
    );
    state.next();
  }, [state, timings, replaceInEditorAtTimestampAndOffsets]);

  return (
    <FindReplacePopover
      state={state}
      onClose={onClose}
      onJumpToMatch={handleJumpToMatch}
      onReplaceAll={handleReplaceAll}
      onReplaceOne={handleReplaceOne}
    />
  );
}
